/*
  Author: Rajeev Dangwal
  Email: rajeev.hmdangwal@gmail.com
  Git: https://github.com/Rajeev5842
*/

import { useState, ReactNode } from "react";
import { Children } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";
import {
  Navigation,
  Pagination,
  FreeMode,
  Mousewheel,
  EffectFade,
} from "swiper/modules";

import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

import "swiper/css";
import "swiper/css/effect-fade";
import "swiper/css/navigation";
import "swiper/css/pagination";

/* ================= TYPES ================= */

type ImageSliderProps = {
  children: ReactNode | ReactNode[];
  className?: string;
  parentClass?: string;
  spaceBetween?: number;
  slidesPerView?: number;
  showArrowsInside?: boolean;
};

/* ================= COMPONENT ================= */

const ImageSlider = ({
  children,
  className = "",
  parentClass = "",
  spaceBetween = 20,
  slidesPerView = 3,
  showArrowsInside = false,
}: ImageSliderProps) => {
  const [swiper, setSwiper] = useState<SwiperType | null>(null);
  const [isEnd, setIsEnd] = useState(false);
  const [isBeginning, setIsBeginning] = useState(true);

  return (
    <div className={`w-full h-full flex items-center gap-2 ${parentClass}`}>
      {/* LEFT ARROW */}
      {!showArrowsInside && (
        <button
          onClick={() => swiper?.slidePrev()}
          className={`flex items-center justify-center
            w-10 h-10 aspect-square
            rounded-full bg-white/70 hover:bg-white
            shadow ring-1 ring-gray-300 transition-opacity
            ${isBeginning ? "opacity-0 pointer-events-none" : ""}`}
        >
          <ArrowBackIosNewIcon fontSize="small" />
        </button>
      )}

      <Swiper
        modules={[Navigation, Pagination, Mousewheel, FreeMode, EffectFade]}
        onSwiper={(instance : SwiperType) => {
          setSwiper(instance);
          setIsBeginning(instance.isBeginning);
          setIsEnd(instance.isEnd);
        }}
        onSlideChange={(instance : SwiperType) => {
          setIsBeginning(instance.isBeginning);
          setIsEnd(instance.isEnd);
        }}
        spaceBetween={spaceBetween}
        slidesPerView={slidesPerView}
        navigation={showArrowsInside}
        freeMode
        mousewheel={{ forceToAxis: true }}
        grabCursor
        className={`h-full w-full ${showArrowsInside ? "inside" : ""} ${
          className || ""
        }`}
        breakpoints={{
          320: { slidesPerView: Math.min(1, slidesPerView) },
          640: { slidesPerView: Math.min(2, slidesPerView) },
          1024: { slidesPerView },
        }}
      >
        {Children.toArray(children).map((child, index) => (
          <SwiperSlide key={index}>{child}</SwiperSlide>
        ))}
      </Swiper>

      {/* RIGHT ARROW */}
      {!showArrowsInside && (
        <button
          onClick={() => swiper?.slideNext()}
          className={`flex items-center justify-center
            w-10 h-10 aspect-square
            rounded-full bg-white/80 hover:bg-gray-50
            shadow ring-1 ring-gray-300 transition-opacity
            ${isEnd ? "opacity-0 pointer-events-none" : ""}`}
        >
          <ArrowForwardIosIcon fontSize="small" />
        </button>
      )}
    </div>
  );
};

export default ImageSlider;
